import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, AtSign, BellOff, Megaphone, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { InboxItem, InboxPage } from '@up2d8/contracts';
import { Badge, Button, Card, EmptyState, LoadingState } from '@up2d8/ui';
import { DeskShell } from './desk/desk-shell';
import { useSession } from '../../shared/session';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { MessagesIcon } from './messages-icon';
import { PushCard } from './push-card';
import { formatDateTime } from '../../shared/format';

/**
 * De meldingenlijst van de medewerker.
 *
 * Eén tik per regel, en die tik doet twee dingen: de melding op gelezen zetten en het bericht
 * openen. Dat scheelt een stap, en het klopt met wat iemand bedoelt als hij op een melding
 * drukt — niemand wil eerst "gelezen" aanvinken.
 *
 * Een ingetrokken melding blijft staan maar opent niet. Weghalen zou een medewerker achterlaten
 * met de vraag of hij zich iets verbeeldde.
 *
 * Elke regel zegt **wat voor soort** melding het is, en niet alleen van wie. Dat was eerst niet zo:
 * elke melding kreeg dezelfde megafoon, en de titel is bij een bedrijfsbericht de titel van dat
 * bericht maar bij een persoonlijk bericht of een vermelding de naam van de afzender. "Ana Peiling"
 * zonder meer leest dan als een bericht met die titel.
 */
export function AppNotificationsPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tabs = useAppTabs();

  const inbox = useInfiniteQuery({
    queryKey: ['notifications'],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data } = await api.GET('/api/v1/notifications', {
        params: { query: { limit: 20, ...(pageParam ? { cursor: pageParam } : {}) } },
      });
      return (data ?? { items: [], nextCursor: null }) as InboxPage;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const markRead = useMutation({
    mutationFn: async (itemId: string) => {
      await api.POST('/api/v1/notifications/{itemId}/read', {
        params: { path: { itemId } },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['unread-count'] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/notifications/read-all', {});
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['unread-count'] });
    },
  });

  const items = inbox.data?.pages.flatMap((page) => page.items) ?? [];
  const unread = items.filter((item) => item.readAt === null).length;

  const open = async (item: InboxItem): Promise<void> => {
    await markRead.mutateAsync(item.id);
    if (item.withdrawnAt !== null) return;

    // Indienen, goedkeuren en afwijzen (Stap 15) gaan over het bericht in de editor, niet in de feed.
    if (item.type === 'post_submitted' || item.type === 'post_rejected') {
      void navigate({ to: '/admin/posts', search: { bereik: undefined, bericht: item.refId } });
      return;
    }
    if (item.refModule === 'communication') {
      void navigate({ to: '/app/posts/$postId', params: { postId: item.refId } });
      return;
    }
    // Een persoonlijk bericht opent het gesprek. Zonder deze regel deed een tik op zo'n melding
    // niets \u2014 ze zette zich op gelezen en bleef staan.
    // Een noodmelding heeft geen eigen pagina: het volledige scherm hangt over de hele app, en
    // dat ziet iemand zodra hij ergens in de app staat (Stap 35).
    if (item.refModule === 'emergency') {
      void navigate({ to: '/app' });
      return;
    }
    /*
      De escalatie opent het bericht in het team (Stap 17, frame 4 -> 2a).

      Niet het bericht zelf (`/app/posts/...`): deze melding gaat niet over wat er in het bericht
      staat, maar over wie het nog moet bevestigen. Wie hier op het gewone berichtscherm landt,
      leest de instructie opnieuw en vindt niet waarvoor hij kwam.
    */
    if (item.refModule === 'reach') {
      void navigate({ to: '/app/team/berichten/$postId', params: { postId: item.refId } });
      return;
    }
    // De overdracht van de vorige ploeg (Stap 39): de melding opent die overdracht.
    if (item.refModule === 'shift-handover') {
      void navigate({ to: '/app/overdracht/$handoverId', params: { handoverId: item.refId } });
      return;
    }
    if (item.refModule === 'messaging_shared') {
      void navigate({
        to: '/app/over-bedrijven/$conversationId',
        params: { conversationId: item.refId },
      });
      return;
    }
    if (item.refModule === 'messaging') {
      void navigate({
        to: '/app/gesprekken/$conversationId',
        params: { conversationId: item.refId },
      });
      return;
    }
    // Een checklist die klaarstaat of te laat is: `refId` is de uitvoering, en die heeft een pagina.
    if (item.refModule === 'tasks') {
      void navigate({ to: '/app/checklists/$runId', params: { runId: item.refId } });
      return;
    }
    // Een nieuwe melding gaat naar wie ze afhandelt (beheer); een gewijzigde stand naar de melder,
    // die ze terugvindt onder "Mijn meldingen".
    if (item.refModule === 'floor-reports') {
      void navigate({ to: item.type === 'floor_report_new' ? '/admin/meldingen' : '/app/melden' });
      return;
    }
    // Agenda en documenten hebben geen pagina per item; de lijst is de plek waar het staat.
    if (item.refModule === 'agenda') {
      void navigate({ to: '/app/agenda' });
      return;
    }
    if (item.refModule === 'documents') {
      void navigate({ to: '/app/documenten' });
    }
  };

  return (
    <DeskShell
      messages={<MessagesIcon />}
      tabs={tabs}
      activeKey="notifications"
      renderLink={renderAppTabLink}
    >
      <div className="flex items-center justify-between gap-element">
        <h1 className="font-heading text-page-title font-semibold text-text-primary">
          {t('notifications.title')}
        </h1>
        {unread > 0 && (
          <Button
            variant="ghost"
            loading={markAllRead.isPending}
            onClick={() => void markAllRead.mutateAsync()}
          >
            {t('notifications.readAll')}
          </Button>
        )}
      </div>

      <PushCard />

      {inbox.isPending ? (
        <LoadingState rows={3} label={t('common.loading')} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            title={t('notifications.emptyTitle')}
            description={t('notifications.emptyHint')}
          />
        </Card>
      ) : (
        items.map((item) => (
          <Card
            key={item.id}
            compact
            className={item.readAt === null ? 'border-brand-primary' : undefined}
          >
            <button
              type="button"
              className="flex w-full items-start gap-element text-left"
              onClick={() => void open(item)}
            >
              <span
                aria-hidden
                className="mt-0.5 rounded-pill bg-brand-primary-tint p-2 text-brand-primary"
              >
                {item.withdrawnAt ? (
                  <BellOff className="h-5 w-5" />
                ) : item.type === 'emergency_alert' || item.type === 'team_escalation' ? (
                  <AlertTriangle className="h-5 w-5" />
                ) : item.type === 'comment_mention' ? (
                  <AtSign className="h-5 w-5" />
                ) : item.type === 'message_received' ? (
                  <MessageSquare className="h-5 w-5" />
                ) : (
                  <Megaphone className="h-5 w-5" />
                )}
              </span>

              <span className="flex-1">
                <span className="block text-small font-semibold uppercase tracking-wide text-brand-primary">
                  {t(`notifications.type.${item.type}`)}
                </span>
                <span className="block font-heading text-title-sm font-semibold text-text-primary">
                  {item.title}
                </span>
                <span className="block text-small text-text-secondary">{item.context}</span>
                <span className="mt-1 block text-small text-text-muted">
                  {formatDateTime(item.createdAt)}
                </span>

                {item.withdrawnAt && (
                  <Badge tone="neutral" className="mt-1">
                    {t('notifications.withdrawn')}
                  </Badge>
                )}
                {!item.withdrawnAt && item.requiresAcknowledgement && (
                  <Badge tone="warning" className="mt-1">
                    {t('feed.ackNeeded')}
                  </Badge>
                )}
              </span>

              {item.readAt === null && (
                <span
                  aria-label={t('notifications.unread')}
                  className="mt-2 h-2.5 w-2.5 shrink-0 rounded-pill bg-brand-primary"
                />
              )}
            </button>
          </Card>
        ))
      )}

      {inbox.hasNextPage && (
        <Button
          variant="secondary"
          loading={inbox.isFetchingNextPage}
          onClick={() => void inbox.fetchNextPage()}
        >
          {t('notifications.more')}
        </Button>
      )}
    </DeskShell>
  );
}
